import { Calendar } from 'primereact/calendar';
import classes from './DateTimeInput.module.scss';
import { useRef } from 'react';
import { formatTime24, parseTime24 } from '@/shared/lib/dateTime';

interface DateTimeInputProps {
    label: string;
    name: string;
    value: Date | null;
    onChange: (e: { value: Date | null }) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    inputRef?: React.Ref<HTMLInputElement>;
    error?: string;
    isRequired?: boolean;
    placeholder?: string;
    disabled?: boolean;
    readonly?: boolean;
    className?: string;
    minDate?: Date;
    maxDate?: Date;
    showIcon?: boolean;
    mask?: string;
    mode?: 'date' | 'time' | 'datetime' | 'month';
    timeOnly?: boolean;
    showTime?: boolean;
    dateFormat?: string;
    hourFormat?: '12' | '24';
    stepHour?: number;
    stepMinute?: number;
    showSeconds?: boolean;
}

/** Отображает поле даты и времени с подписью и ошибкой. */
export const DateTimeInput: React.FC<DateTimeInputProps> = (
    {
        label,
        name,
        value,
        onChange,
        onBlur,
        inputRef,
        error,
        isRequired = false,
        placeholder,
        disabled = false,
        readonly = false,
        className = '',
        minDate,
        maxDate,
        showIcon = true,
        mask = '',
        mode = 'date',
        timeOnly = false,
        showTime = false,
        dateFormat,
        hourFormat = '24',
        stepHour = 1,
        stepMinute = 15,
        showSeconds = false,
    }
) => {
    const actualTimeOnly = mode === 'time' || timeOnly;
    const actualShowTime = mode === 'time' || mode === 'datetime' || showTime;
    const minuteTimeOnly = actualTimeOnly && hourFormat === '24' && !showSeconds;

    const timeBounds = useRef({
        minDate,
        maxDate,
        onChange
    });

    timeBounds.current = {
        minDate,
        maxDate,
        onChange
    };
    const timeDate = value ?? minDate ?? maxDate ?? parseTime24(formatTime24(Date.now()))!;

    const actualView = mode === 'month'
        ? 'month'
        : 'date';

    const actualDateFormat = dateFormat || (mode === 'time'
        ? 'dd.mm.yy'
        : (mode === 'month'
            ? 'mm.yy'
            : 'dd.mm.yy'));

    const defaultPlaceholder = actualTimeOnly
        ? 'чч:мм'
        : actualShowTime
            ? 'дд.мм.гггг чч:мм'
            : 'дд.мм.гггг';

    const labelClass = `${classes.label} ${isRequired
        ? classes.required
        : ''}`;

    const inputClass = `${classes.dateTimeInput} ${error
        ? classes.error
        : ''} ${className}`;

    /** Читает время на том же календарном дне, что и границы выбора */
    const parseMinuteTime = (text: string): Date => {
        const parsed = parseTime24(text);

        if (!parsed) throw new Error('Некорректное время');

        return new Date(timeDate.getFullYear(), timeDate.getMonth(), timeDate.getDate(), parsed.getHours(), parsed.getMinutes());
    };

    /** Проверяет актуальные границы для событий маски после смены даты */
    const changeValue = (next: Date | null) => {
        const current = timeBounds.current;

        if (minuteTimeOnly && next) {
            const time = formatTime24(next.getTime());

            if (current.minDate && time < formatTime24(current.minDate.getTime())) return;
            if (current.maxDate && time > formatTime24(current.maxDate.getTime())) return;
        }

        current.onChange({ value: next });
    };

    return (
        <div className={classes.wrapper}>
            <label
                htmlFor={name}
                className={labelClass}
            >
                {label}
            </label>
            <Calendar
                inputId={name}
                name={name}
                inputRef={inputRef}
                value={value}
                viewDate={minuteTimeOnly
                    ? timeDate
                    : undefined
                }
                parseDateTime={minuteTimeOnly
                    ? parseMinuteTime
                    : undefined
                }
                onChange={(event) => changeValue(event.value ?? null)}
                onBlur={onBlur}
                disabled={disabled}
                readOnlyInput={readonly}
                className={inputClass}
                placeholder={placeholder || defaultPlaceholder}
                minDate={minDate}
                maxDate={maxDate}
                mask={mask || (actualTimeOnly && hourFormat === '24' && !showSeconds
                    ? '99:99'
                    : ''
                )}
                showIcon={showIcon}
                view={actualView}
                timeOnly={actualTimeOnly}
                showTime={actualShowTime}
                dateFormat={actualDateFormat}
                hourFormat={hourFormat}
                stepHour={stepHour}
                stepMinute={stepMinute}
                showSeconds={showSeconds}
                invalid={Boolean(error)}
                pt={{
                    input: {
                        root: {
                            inputMode: actualTimeOnly
                                ? 'numeric'
                                : undefined,
                            'aria-required': isRequired,
                            'aria-invalid': Boolean(error),
                            'aria-describedby': error
                                ? `${name}-error`
                                : undefined,
                        },
                    }
                }}
                locale="ru"
                showButtonBar={false}
            />
            {error && (
                <small
                    id={`${name}-error`}
                    className={classes.errorText}
                    role="alert"
                >
                    {error}
                </small>
            )}
        </div>
    );
};
